/**
 * Secure Storage Migration and Maintenance Utilities
 * 
 * This module provides utilities for managing secure storage data,
 * including migration, cleanup, and maintenance operations.
 */

import secureStorage from './secureStorage';

/**
 * Migration utilities for secure storage
 */
export const migrationUtils = {
    /**
     * Get current storage version
     * @returns {string} Current version or 'unknown'
     */
    getCurrentVersion() {
        try {
            const credentials = secureStorage.getStoredCredentials();
            return credentials?.version || 'unknown';
        } catch (error) {
            return 'unknown';
        }
    },

    /**
     * Check if migration is needed
     * @returns {boolean} True if migration is needed
     */
    needsMigration() {
        const currentVersion = this.getCurrentVersion();
        const latestVersion = '1.0';
        return currentVersion !== latestVersion;
    },

    /**
     * Perform migration if needed
     * @returns {boolean} True if migration was successful or not needed
     */
    migrate() {
        if (!this.needsMigration()) {
            return true;
        }

        try {
            // Future migration logic would go here
            return true;
        } catch (error) {
            return false;
        }
    }
};

/**
 * Maintenance utilities for secure storage
 */
export const maintenanceUtils = {
    /**
     * Clean up expired credentials
     * @returns {boolean} True if cleanup was successful
     */
    cleanupExpired() {
        try {
            const credentials = secureStorage.getStoredCredentials();
            if (!credentials) {
                return true; // Nothing to clean up
            }

            const now = new Date();
            if (now > credentials.expiresAt) {
                secureStorage.clearStoredCredentials();
            }
            
            return true;
        } catch (error) {
            return false;
        }
    },

    /**
     * Get storage health status
     * @returns {Object} Health status information
     */
    getHealthStatus() {
        try {
            const credentials = secureStorage.getStoredCredentials();
            const info = secureStorage.getStoredCredentialsInfo();
            
            if (!credentials && !info) {
                return {
                    status: 'healthy',
                    message: 'No stored credentials',
                    hasData: false
                };
            }

            if (!credentials && info) {
                return {
                    status: 'corrupted',
                    message: 'Corrupted data detected',
                    hasData: true,
                    recommendation: 'Clear storage and re-authenticate'
                };
            }

            const now = new Date();
            const isExpired = now > credentials.expiresAt;
            const expiresInHours = Math.floor((credentials.expiresAt - now) / (1000 * 60 * 60));

            if (isExpired) {
                return {
                    status: 'expired',
                    message: 'Credentials have expired',
                    hasData: true,
                    recommendation: 'Re-authenticate to refresh credentials'
                };
            }

            if (expiresInHours < 24) {
                return {
                    status: 'warning',
                    message: `Credentials expire in ${expiresInHours} hours`,
                    hasData: true,
                    recommendation: 'Consider extending expiry or re-authenticating'
                };
            }

            return {
                status: 'healthy',
                message: `Credentials valid for ${expiresInHours} hours`,
                hasData: true
            };

        } catch (error) {
            return {
                status: 'error',
                message: error.message,
                hasData: false,
                recommendation: 'Clear storage and re-authenticate'
            };
        }
    },

    /**
     * Perform full maintenance check
     * @returns {Object} Maintenance report
     */
    performMaintenance() {
        const report = {
            timestamp: new Date(),
            actions: [],
            status: 'success'
        };

        try {
            // 1. Check for migration needs
            if (migrationUtils.needsMigration()) {
                const migrated = migrationUtils.migrate();
                report.actions.push({
                    action: 'migration',
                    success: migrated,
                    message: migrated ? 'Migration completed' : 'Migration failed'
                });
                if (!migrated) report.status = 'partial';
            }

            // 2. Clean up expired data
            const cleaned = this.cleanupExpired();
            report.actions.push({
                action: 'cleanup',
                success: cleaned,
                message: cleaned ? 'Cleanup completed' : 'Cleanup failed'
            });
            if (!cleaned) report.status = 'partial';

            // 3. Health check
            const health = this.getHealthStatus();
            report.health = health;
            report.actions.push({
                action: 'health_check',
                success: health.status !== 'error',
                message: health.message
            });

            return report;

        } catch (error) {
            report.status = 'error';
            report.error = error.message;
            return report;
        }
    }
};

/**
 * Debug utilities for development and troubleshooting
 */
export const debugUtils = {
    /**
     * Get detailed storage information for debugging
     * @returns {Object} Debug information
     */
    getDebugInfo() {
        try {
            const rawData = localStorage.getItem('heidi_secure_login_credentials');
            const credentials = secureStorage.getStoredCredentials();
            const info = secureStorage.getStoredCredentialsInfo();
            const health = maintenanceUtils.getHealthStatus();

            return {
                timestamp: new Date(),
                hasRawData: !!rawData,
                rawDataSize: rawData ? rawData.length : 0,
                hasDecryptedData: !!credentials,
                hasMetadata: !!info,
                health,
                credentials: credentials ? {
                    username: credentials.username,
                    storedAt: credentials.storedAt,
                    expiresAt: credentials.expiresAt,
                    isExpired: new Date() > credentials.expiresAt
                } : null,
                storageKeys: Object.keys(localStorage)
                    .filter(key => key.startsWith('heidi_secure_'))
            };
        } catch (error) {
            return {
                timestamp: new Date(),
                error: error.message,
                hasError: true
            };
        }
    },


    /**
     * Reset all secure storage data (for debugging)
     * @returns {boolean} Success status
     */
    resetStorage() {
        try {
            Object.keys(localStorage)
                .filter(key => key.startsWith('heidi_secure_'))
                .forEach(key => localStorage.removeItem(key));
            
            return true;
        } catch (error) {
            return false;
        }
    }
};

/**
 * Initialize maintenance on app load
 */
export const initializeMaintenance = () => {
    try {
        // Perform automatic maintenance on app load
        const report = maintenanceUtils.performMaintenance();
        
        return report;
    } catch (error) {
        return null;
    }
};

// Export all utilities
export default {
    migration: migrationUtils,
    maintenance: maintenanceUtils,
    debug: debugUtils,
    initialize: initializeMaintenance
};
